import {
  Component,
  HostListener,
  inject,
  input,
  OnDestroy,
  OnInit,
  output
} from '@angular/core';
import {FileProgressBarStatusComponent} from "../file-progress-bar-status/file-progress-bar-status.component";
import {MatIcon} from "@angular/material/icon";
import {MatIconButton} from "@angular/material/button";
import {MatTooltip} from "@angular/material/tooltip";
import {FilesService} from "../../../../core/services/files.service";
import {FileUploadService} from "../../../../core/services/file-upload.service";
import {ValidationFilesService} from "../../../../core/services/validation-files.service";
import {PreviewFileUrlsStateService} from "../../../../core/services/preview-file-urls-state.service";
import {defaultFileParams} from "../../../../core/models/default-file-params";
import {SnackbarService} from "../../../../core/services/snackbar.service";
import {EditModeService} from "../../../../core/services/edit-mode.service";
import {AttachFileParams, FileUploadParams, UploadFileData} from "../../../../core/types/file.types";
import {contentPaste, contentPasteGo} from "./configs/file-upload-card-config";
import {PasteEnabledStateService} from "./services/paste-enabled-state.service";

@Component({
  selector: 'app-file-upload-card',
  standalone: true,
  imports: [
    FileProgressBarStatusComponent,
    MatIcon,
    MatIconButton,
    MatTooltip
  ],
  templateUrl: './file-upload-card.component.html',
  styleUrl: './file-upload-card.component.scss'
})
export class FileUploadCardComponent implements OnInit, OnDestroy {
  fileUploadParams = input.required<FileUploadParams>();
  isEditModeActive = input<boolean>(false);
  isNeedPreview = input<boolean>(false);
  remainingFileSlots = input<number | null>(null);
  isMultiple = input<boolean>(true);
  attachFile = output<AttachFileParams>();

  public pasteEnabledStateService: PasteEnabledStateService = inject(PasteEnabledStateService);
  private filesService: FilesService = inject(FilesService);
  private fileUploadService: FileUploadService = inject(FileUploadService);
  private validationFilesService: ValidationFilesService = inject(ValidationFilesService);
  private previewFileUrlsStateService: PreviewFileUrlsStateService = inject(PreviewFileUrlsStateService);
  private snackbarService: SnackbarService = inject(SnackbarService);
  private editModeService: EditModeService = inject(EditModeService);

  public selectedFiles: Array<File> = [];
  public isHover: boolean = false;

  @HostListener('document:paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    this.pasteEnabledStateService.isPasteEnabled(this.fileUploadParams().type) && this.handlePaste(event);
  }

  public ngOnInit(): void {
    this.pasteEnabledStateService.setPasteEnabled(this.fileUploadParams().type, false);
  }

  public ngOnDestroy() {
    this.editModeService.setEditMode(true);
    this.pasteEnabledStateService.deletePasteEnabledState(this.fileUploadParams().type);
  }

  public getClasses(): string {
    return this.isHover ? 'hover' : '';
  }

  public async handleFileDropped(event: DragEvent): Promise<void> {
    event.preventDefault();
    this.isHover = false;
    const files = event.dataTransfer?.files;
    if (!files?.length) { return; }

    await this.processAndUploadFiles(Array.from(files));
  }

  public handleDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isHover = true;
  }

  public handleDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isHover = false;
  }

  public getTooltip(): string {
    return this.pasteEnabledStateService.isPasteEnabled(this.fileUploadParams().type) ?
      contentPasteGo.text :
      contentPaste.text;
  }

  public getIcon(): string {
    return this.pasteEnabledStateService.isPasteEnabled(this.fileUploadParams().type) ?
      contentPasteGo.icon :
      contentPaste.icon
  }

  public togglePasteMode(): void {
    const isPasteEnabled = this.pasteEnabledStateService.isPasteEnabled(this.fileUploadParams().type);
    this.pasteEnabledStateService.setPasteEnabled(this.fileUploadParams().type, !isPasteEnabled);
    this.pasteEnabledStateService.isPasteEnabled(this.fileUploadParams().type) &&
      this.snackbarService.showMessage({ message: contentPasteGo.text });
  }

  public async handleFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = input?.files;
    if (!files?.length) { return; }

    await this.processAndUploadFiles(Array.from(files));
  }

  private async handlePaste(event: ClipboardEvent): Promise<void> {
    const files = Array.from(event.clipboardData?.items || [])
      .filter(item => item.kind === 'file')
      .map(item => item.getAsFile())
      .filter(Boolean) as File[];

    if (!files.length) return;

    event.preventDefault();
    await this.processAndUploadFiles(files);
    this.pasteEnabledStateService.setPasteEnabled(this.fileUploadParams().type, false);
  }

  private async processAndUploadFiles(files: Array<File>): Promise<void> {
    const allowedFiles = this.filesService.filterAllowedFiles(files, this.fileUploadParams().type);
    if (!allowedFiles.length) {
      this.selectedFiles = [];
      return;
    }

    const remainingFileSlots = this.remainingFileSlots();
    if (remainingFileSlots === null) {
      return this.uploadAllFiles(allowedFiles);
    }


    if (remainingFileSlots <= 0) {
      this.selectedFiles = [];
      this.showLimitReachedMessage();
      return;
    }

    const isLimit = remainingFileSlots < allowedFiles.length;
    isLimit && this.showPartialUploadMessage(allowedFiles.length, remainingFileSlots);
    const filesToUpload = isLimit ?
      allowedFiles.slice(0, remainingFileSlots) :
      allowedFiles;

    await this.uploadAllFiles(filesToUpload);
  }

  private async uploadFile(selectedFile: File): Promise<void> {
    const formData = this.filesService.createFormData({
      file: selectedFile,
      fileType: 'file'
    });

    const uploadFileData: UploadFileData = {
      formData: formData,
      entityId: this.fileUploadParams().recordId,
      entityFileType: this.fileUploadParams().entityFileType
    };

    await this.fileUploadService.uploadFile(uploadFileData)
      .then((fileParams) => {
        this.updateFiles(fileParams.id);
        if (this.isNeedPreview()) {
          this.validationFilesService.isImage(fileParams.filename) &&
            this.previewFileUrlsStateService.addPreviewFileUrl(this.fileUploadParams().type, fileParams.id);
        }
      })
      .catch(() => defaultFileParams);
  }

  private async updateFiles(fileId: number): Promise<void> {
    const attachFileParams: AttachFileParams = {
      parentId: this.fileUploadParams().parentId,
      fileId: fileId,
      type: this.fileUploadParams().type
    };

    this.attachFile.emit(attachFileParams);
  }

  private async uploadAllFiles(files: Array<File>): Promise<void> {
    this.selectedFiles = files;
    await Promise.all(files.map(file => this.uploadFile(file)));
    this.selectedFiles = [];
  }

  private showLimitReachedMessage(): void {
    this.snackbarService.showMessage({
      type: 'error',
      message: 'Максимальное количество файлов достигнуто. Удалите лишние файлы, чтобы загрузить новые.'
    });
  }

  private showPartialUploadMessage(totalCount: number, remainingSlots: number): void {
    const excessCount = totalCount - remainingSlots;
    this.snackbarService.showMessage({
      type: 'error',
      message: `Доступно ещё ${remainingSlots} слот${remainingSlots === 1 ? 'ов' : 'ов'} для файлов. ${excessCount} файл${excessCount === 1 ? ' не был загружен' : 'a не были загружены'}.`
    });
  }
}
